import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const SidebarNav = ({ currentPath }) => {
    const { t } = useTranslation();
    const { logout } = useAuth();

    // Theme variables
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";
    const accentColor = "text-blue-400";

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    // Navigation items
    const navItems = [
        {
            path: '/developer/dashboard',
            name: t('developerPage.dashboard', 'Dashboard'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
                </svg>
            )
        },
        {
            path: '/developer/analytics',
            name: t('developerPage.analytics', 'Analytics'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
            )
        },
        {
            path: '/developer/messages',
            name: t('developerPage.messages', 'Messages'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                </svg>
            )
        },
        {
            path: '/developer/resumes',
            name: t('developerPage.resumes', 'Resumes'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
            )
        },
        {
            path: '/developer/blog',
            name: t('developerPage.blog', 'Blog'),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path>
                </svg>
            )
        }
    ];

    return (
        <div className="fixed left-0 top-0 bottom-0 w-64 bg-black backdrop-blur-sm border-r border-blue-900/20 pt-24 pb-6 overflow-y-auto shadow-xl z-10">
            <div className="px-4">
                <h2 className={`${textPrimary} text-xl font-medium mb-6 px-2`}>Developer Panel</h2>
                <nav className="space-y-1">
                    {navItems.map(item => {
                        const isActive = currentPath === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center px-3 py-3 rounded-md transition-all duration-200 ${isActive
                                    ? 'bg-blue-900/20 text-blue-300 border-l-2 border-blue-400 pl-2'
                                    : `${textSecondary} hover:bg-gray-900/80 hover:${accentColor}`
                                    }`}
                            >
                                <span className={`mr-3 ${isActive ? accentColor : ''}`}>{item.icon}</span>
                                <span className="font-medium">{item.name}</span>
                                {isActive && (
                                    <span className="ml-auto">
                                        <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>
            </div>

            <div className="mt-auto pt-6 px-6">
                <div className="border-t border-gray-800/40 pt-6">
                    <button
                        onClick={handleLogout}
                        className="flex items-center text-sm text-red-400 hover:text-red-300 group focus:outline-none w-full"
                    >
                        <svg className="w-4 h-4 mr-2 group-hover:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                        </svg>
                        {t('developerPage.logout', 'Logout')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SidebarNav; 